"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RatingFieldLogic = void 0;
const types_1 = require("@smartsuite/types");
const clipboard_1 = require("../../utils/clipboard");
const fields_1 = require("../../utils/fields");
const google_sheet_1 = require("../../utils/google-sheet");
const migration_1 = require("../../utils/migration");
const detailed_decimal_1 = require("../../formatters/detailed-decimal");
const intl_1 = require("../../locale/intl");
const rating_fieldtype_1 = require("../../safe-field-value/numder-fields/rating-field/rating.fieldtype");
const texts_1 = require("../../texts");
const number_1 = require("../../utils/number");
const base_number_fieldtype_1 = require("../base-fields/base-number.fieldtype");
class RatingFieldLogic extends base_number_fieldtype_1.BaseNumberFieldLogic {
    constructor() {
        super(...arguments);
        this.name = "ratingfield";
        this.label = (0, intl_1.$t)(texts_1.ratingFieldText.rating);
        this.icon = "rating-field";
        this.iconColor = "#6A849B";
        this.placeholder = (0, intl_1.$t)(texts_1.ratingFieldText.enter_number_here);
        this.searchSynonyms = [
            (0, intl_1.$t)(texts_1.ratingFieldText.score),
            (0, intl_1.$t)(texts_1.ratingFieldText.nps),
        ];
        this.groupByMode = "grid";
        this.isNumber = true;
        this.fieldValueGetter = new rating_fieldtype_1.RatingFieldValueGetter();
        this.clipboardPasteRules = {
            ratingfield: clipboard_1.clipboardToSelf,
            numberfield: clipboard_1.clipboardTextToRating,
            currencyfield: clipboard_1.clipboardTextToRating,
            percentfield: clipboard_1.clipboardTextToRating,
            autonumberfield: clipboard_1.clipboardNumberToRating,
            rollupfield: clipboard_1.clipboardTextToRating,
            votefield: clipboard_1.clipboardTextToRating,
            countfield: clipboard_1.clipboardTextToRating,
            numbersliderfield: clipboard_1.clipboardTextToRating,
            percentcompletefield: clipboard_1.clipboardTextToRating,
            textfield: clipboard_1.clipboardTextToRating,
            recordtitlefield: clipboard_1.clipboardTextToRating,
            richtextareafield: clipboard_1.clipboardTextToRating,
            textareafield: clipboard_1.clipboardTextToRating,
            unknownfield: clipboard_1.clipboardTextToRating,
        };
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            enablePasteEventInEditMode: true,
            enableCopyEventInEditMode: true,
            deleteValueOnFirstKeypress: true,
            switchToInputControl: "OnCellSelect",
            saveKeys: types_1.navigationKeys,
            editOnStartTyping: false,
            editorWidth: "100%",
            spaceKeyOpensRecordModal: () => true,
            needUseDefaultValue: (value, isDirty) => {
                const isNeedUseDefaultValue = (0, fields_1.needUseDefaultFieldValue)(field, value);
                return isDirty && isNeedUseDefaultValue;
            },
        };
    }
    isValueEqual(fieldValue1, fieldValue2) {
        return fieldValue1 === fieldValue2;
    }
    valueToString(value) {
        return value ? value.toString() : "";
    }
    valueToExport(fieldValue) {
        if (!fieldValue) {
            return "0";
        }
        return this.valueToString(fieldValue);
    }
    getAggregateDisplayValue(fieldValue, field, userPreferences) {
        var _a, _b;
        return (0, detailed_decimal_1.convertNumberToFormatedString)({
            value: (_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.toString()) !== null && _a !== void 0 ? _a : "",
            precision: 1,
            decimalSeparator: (0, number_1.getDecimalSeparator)((_b = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale) === null || _b === void 0 ? void 0 : _b.value),
            thousandSeparator: "",
        });
    }
    valueToGoogleSheet(fieldValue) {
        const value = fieldValue !== null && fieldValue !== void 0 ? fieldValue : 0;
        return (0, google_sheet_1.makeRowDataValues)(value, "numberValue");
    }
    valueToExcelExport(fieldValue) {
        return {
            t: "n",
            v: this.valueToExport(fieldValue),
        };
    }
    processFieldValue(record, slug, comparison, field) {
        const fieldValue = super.processFieldValue(record, slug, comparison, field);
        const filterComparisons = ["is_empty", "is_not_empty"];
        if (filterComparisons.includes(comparison) && fieldValue === 0) {
            return "";
        }
        return fieldValue && +fieldValue;
    }
    getEmptyValue() {
        return null;
    }
    shouldCleanUpEmptyInLookupFilter(comparison) {
        return ["is_empty", "is_not_empty"].includes(comparison);
    }
    getInitialFieldValue(startValue, _field, _userPreferences) {
        return startValue && !isNaN(Number(startValue))
            ? +startValue
            : this.getEmptyValue();
    }
    getGridColumnSettings(field) {
        return {
            ...super.getGridColumnSettings(field),
            contentAlignment: "centered",
        };
    }
    isImportedValueValid(parsedValue, dataValue) {
        return !(!parsedValue && parsedValue !== 0 && dataValue);
    }
    prepareValueForClipboard(fieldValue, srcField) {
        var _a;
        const stringValue = this.valueToString(fieldValue);
        return {
            text: stringValue ? `${stringValue}/${(_a = srcField.params.scale) !== null && _a !== void 0 ? _a : 1}` : "",
            rawValue: fieldValue,
        };
    }
    getLinkedRecordFieldSmartWidth(field) {
        let width = { defaultWidht: 170, minWidth: 90 };
        if (field.params.scale && field.params.scale <= 5) {
            width = {
                defaultWidht: 110,
                minWidth: 55,
            };
        }
        if (field.params.scale === 6) {
            width = {
                defaultWidht: 140,
                minWidth: 70,
            };
        }
        return {
            defaultWidht: width.defaultWidht,
            minWidth: width.minWidth,
        };
    }
    migrateField(fieldTo, fieldFrom) {
        var _a;
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        return (0, migration_1.migrateDisplayFormat)(migratedField, types_1.ratingDisplayFormats, (_a = fieldTo.params.display_format) !== null && _a !== void 0 ? _a : "");
    }
    filterToRecordValue(filterValue, comparison, field) {
        var _a;
        if (comparison !== "is_equal_to" || !(0, number_1.isNumeric)(filterValue)) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        const value = Number(filterValue);
        if (value < 0 || value > ((_a = field.params.scale) !== null && _a !== void 0 ? _a : 1)) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        return value;
    }
    getContentSize(_fieldValue, field) {
        var _a;
        const iconSize = 20;
        return ((_a = field.params.scale) !== null && _a !== void 0 ? _a : 1) * iconSize;
    }
}
exports.RatingFieldLogic = RatingFieldLogic;
